import { CameraPlatformSchema } from '../../../renderer/src/camera-platform-contract';
import { MapStyleSchema } from '../../../renderer/src/map-style-contract';
import { CameraGimbalSchema } from '../../../renderer/src/camera-gimbal-contract';
import { ExportRangeSchema } from '../../../renderer/src/export-range-contract';
import { retimeExportRange } from './export-range-editor';
import { z } from 'zod';
import { automaticPlaceTitle, PlaceTitleSettingsSchema, placeTitleForExport } from './place-title-settings';
import { CinematicSequenceSchema } from '../../../renderer/src/cinematic-sequence-contract';
import { emptyCinematicSequence } from '../../../renderer/src/cinematic-sequence';
import { CameraAngleSchema } from '../../../renderer/src/camera-angle-contract';
import { DepthOfFieldSchema } from '../../../renderer/src/depth-of-field-contract';
import { DEFAULT_DEPTH_OF_FIELD, enabledDepthOfField } from '../../../renderer/src/depth-of-field';
import { resetProAppearance } from '../../../shared/studio-capabilities';
import { VideoPresentationSchema } from '../../../renderer/src/presentation-contract';
import { TrackAppearanceSchema } from '../../../renderer/src/track-appearance-contract';
import { defaultTrackAppearance } from '../../../renderer/src/track-appearance';
import { presentationSupportsPreset } from '../../../renderer/src/video-presentation';
import { CAMERA_DISTANCE_IDS } from '../domain/camera-distance';
import { ROUTE_LINE_STYLES } from '../domain/route-line-style';
import { filmDuration, isFilmDuration } from '../domain/film-timing';
import { isPlaybackSecondsPerKilometer } from '../domain/playback-pace';
import { retimeFilmProgress } from '../domain/camera-plan';
import type { Ride } from '../domain/ride';
import { defaultImageRecipe } from '../route-images/model';
import { readCompositionOptions } from '../render/composition-options';
import { readVideoPresetPreference } from '../services/video-presentation-preferences';
import type { VideoRequest } from '../services/local-video';
import type { RenderPresetId } from '../domain/render-preset';
import { retimeCameraKeyframes } from '../domain/camera-keyframes';

const presetSchema = z.enum(['landscape-16x9', 'social-4x5', 'vertical-9x16']);
export const videoPreferenceSchema = z.object({
  preset: presetSchema, presentation: VideoPresentationSchema, chosen: z.boolean(),
}).refine(value => presentationSupportsPreset(value.presentation, value.preset), 'Appearance does not support this aspect ratio.');
export const studioDraftSchema = z.object({
  freshEdit: z.boolean().optional(),
  placeTitle: PlaceTitleSettingsSchema.default(automaticPlaceTitle),
  sourceKey: z.string().min(1),
  exportRange: ExportRangeSchema.nullable().default(null),
  keepSourcePace: z.boolean().default(false),
  title: z.string().min(1).max(120).refine(value => value.trim().length > 0),
  cameraStyleVersion: z.union([z.literal(3), z.literal(4)]).default(3),
  camera: CameraPlatformSchema, distance: z.enum(CAMERA_DISTANCE_IDS),
  cameraGimbal: CameraGimbalSchema.default('glide'),
  cameraAngle: CameraAngleSchema.nullable().default(null),
  cinematicEnabled: z.boolean().default(false),
  cinematicSequence: CinematicSequenceSchema.default(emptyCinematicSequence),
  depthOfField: DepthOfFieldSchema.default(() => ({ ...DEFAULT_DEPTH_OF_FIELD })),
  line: z.string().refine(value => ROUTE_LINE_STYLES.some(style => style.id === value)),
  mapStyle: MapStyleSchema,
  trackAppearance: TrackAppearanceSchema,
  hidePlaces: z.boolean(), terrainExaggeration: z.number().min(1).max(3),
  unitSystem: z.enum(['metric', 'imperial']),
  timing: z.object({ mode: z.enum(['pace', 'duration']),
    pace: z.number().refine(isPlaybackSecondsPerKilometer), targetSeconds: z.number().refine(isFilmDuration) }),
  preset: presetSchema,
  appearances: z.record(z.string(), videoPreferenceSchema),
  progress: z.number().min(0).max(1),
  imageRecipe: z.object({ treatment: z.enum(['terra-terrain', 'satellite-terrain']).transform(() => 'terra-terrain' as const), rotation: z.number().min(0).max(360),
    tilt: z.number().min(20).max(80), terrainHeight: z.number().min(1).max(3), unitSystem: z.enum(['metric', 'imperial']) }),
  sections: z.record(z.string(), z.boolean()),
});
export type StudioDraft = z.infer<typeof studioDraftSchema>;
export const appearanceKey = (preset: RenderPresetId, social: boolean) => `${social ? 2 : 1}:${preset}`;

/** Uses original-byte and selected-track identity supplied by the importers. */
export function sourceKey(ride: Ride): string { return ride.sourceFingerprint ?? `session:${ride.id}`; }

export function defaultStudioDraft(ride: Ride, search: string): StudioDraft {
  const composition = readCompositionOptions(search);
  const appearances: StudioDraft['appearances'] = {};
  for (const social of [true, false]) for (const preset of ['landscape-16x9', 'social-4x5', 'vertical-9x16'] as const) {
    appearances[appearanceKey(preset, social)] = readVideoPresetPreference(preset, social);
  }
  let unitSystem = composition.unitSystem;
  try { if (!new URLSearchParams(search).has('units')) unitSystem = localStorage.getItem('ride-trace.units') === 'imperial' ? 'imperial' : 'metric'; } catch { /* Reported by draft storage. */ }
  return { sourceKey: sourceKey(ride), title: ride.title,
    placeTitle: composition.placeTitle ? { enabled: true, ...composition.placeTitle } : automaticPlaceTitle(),
    cameraStyleVersion: 4, camera: composition.cameraStyleId, distance: composition.cameraDistanceId, line: composition.lineStyleId,
    cameraGimbal: composition.cameraGimbal ?? 'glide',
    cameraAngle: composition.cameraAngle ?? null,
    cinematicEnabled: false, cinematicSequence: emptyCinematicSequence(),
    depthOfField: composition.depthOfField ?? { ...DEFAULT_DEPTH_OF_FIELD },
    mapStyle: composition.mapStyleId, trackAppearance: composition.trackAppearance ?? defaultTrackAppearance(composition.lineStyleId, composition.cameraStyleId, composition.mapStyleId),
    hidePlaces: composition.hidePlaces, terrainExaggeration: composition.terrainExaggeration, unitSystem,
    timing: { mode: 'pace', pace: 3, targetSeconds: 90 }, preset: readVideoPresetPreference().preset,
    appearances, exportRange: null, keepSourcePace: false, progress: 0, imageRecipe: defaultImageRecipe(unitSystem), sections: {} };
}

export function changeStudioTiming(draft: StudioDraft, timing: StudioDraft['timing'], kilometers: number): StudioDraft {
  const tracks = draft.cinematicSequence.cameraTracks;
  return { ...draft, timing, cinematicSequence: tracks ? { ...draft.cinematicSequence,
    cameraTracks: retimeCameraKeyframes(tracks, filmDuration(kilometers, draft.timing), filmDuration(kilometers, timing)) } : draft.cinematicSequence,
    keepSourcePace: false, exportRange: retimeExportRange(draft.exportRange, filmDuration(kilometers, draft.timing), filmDuration(kilometers, timing)), progress: retimeFilmProgress(draft.progress,
    filmDuration(kilometers, draft.timing), filmDuration(kilometers, timing)) };
}

/** Preview and submission use the same configuration, including the current title. */
export function studioVideoRequest(ride: Ride, draft: StudioDraft, social: boolean, kilometers: number): VideoRequest {
  const appearance = draft.appearances[appearanceKey(draft.preset, social)]!;
  const placeTitle = placeTitleForExport(draft.placeTitle);
  return { cameraStyleVersion: draft.cameraStyleVersion, ...(draft.exportRange ? { exportRange: { inFrame: draft.exportRange.inFrame, outFrame: draft.exportRange.outFrame,
    ...(!draft.keepSourcePace && draft.timing.mode === 'duration' ? { durationSeconds: draft.timing.targetSeconds } : {}) } } : {}), ride: { ...ride, title: draft.title }, preset: draft.preset, camera: draft.camera, distance: draft.distance,
    ...(placeTitle ? { placeTitle } : {}),
    ...(draft.cinematicEnabled && (draft.cinematicSequence.waypoints.length || draft.cinematicSequence.cameraTracks) ? { cinematicSequence: draft.cinematicSequence } : {}),
    ...(enabledDepthOfField(draft.depthOfField) ? { depthOfField: enabledDepthOfField(draft.depthOfField) } : {}),
    cameraGimbal: draft.cameraGimbal,
    ...(draft.cameraAngle === null ? {} : { cameraAngle: draft.cameraAngle }),
    line: draft.line, mapStyle: draft.mapStyle, trackAppearance: { ...draft.trackAppearance },
    hidePlaces: draft.hidePlaces, terrainExaggeration: draft.terrainExaggeration, unitSystem: draft.unitSystem,
    durationSeconds: filmDuration(kilometers, draft.timing),
    presentation: { ...appearance.presentation } };
}

/** Keeps map, track, layout and information visibility when removing paid finishing. */
export function resetStudioProSettings(draft: StudioDraft, social = true): StudioDraft {
  const key = appearanceKey(draft.preset, social);
  const appearance = draft.appearances[key]!;
  const sequence = { ...draft.cinematicSequence };
  delete sequence.cameraTracks;
  return { ...draft, cameraGimbal: 'dynamic', cameraAngle: null, depthOfField: { ...draft.depthOfField, enabled: false },
    cinematicSequence: { ...sequence, waypoints: draft.cinematicSequence.waypoints.map(waypoint => ({ ...waypoint,
      overrides: { ...waypoint.overrides, ...(waypoint.overrides.presentationAppearance ? { presentationAppearance: {} } : {}), ...(waypoint.overrides.cameraGimbal ? { cameraGimbal: 'dynamic' as const } : {}), ...('cameraAngle' in waypoint.overrides ? { cameraAngle: null } : {}),
        ...(waypoint.overrides.depthOfField ? { depthOfField: { ...waypoint.overrides.depthOfField, enabled: false } } : {}) } })) },
    appearances: { ...draft.appearances, [key]: { ...appearance, presentation: resetProAppearance(appearance.presentation) } } };
}
